import React, { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import Recorder from './Recorder';
import JiraDashboard from './JiraDashboard';
import { FastIssuesView } from './FastIssuesView';
import Settings from './Settings/Settings';
import AdvancedSettings from './Settings/AdvancedSettings';
import ErrorBoundary from './ErrorBoundary';
import usePushToTalk from '../hooks/usePushToTalk';
import { safeUrl } from '../utils/safeUrl';
import { escapeRegExp } from '../utils/regex';
import { useRecording } from '../contexts/RecordingContext';
import { useDialogue } from '../contexts/DialogueContext';
import DialogueMenu from './DialogueMenu';
import AllApps from './AllApps';
import { issueCacheSingleton } from '../cache/issueCache';
import InsightsBadge from './MeetingInsights/InsightsBadge';

const isIosApp = new URLSearchParams(window.location.search).get('ios') === '1';

const HINT_MESSAGES = [
  { display: "What's due today?", input: "What my tasks are due today?" },
  { display: "What's due next week?", input: "What my tasks are due next week?" },
  { display: "What's overdue?", input: "What my tasks are overdue?" },
  { display: "What's severely overdue?", input: "What my tasks are severely overdue (over 14 days)?" }
];

const JIRA_BASE_URL = process.env.REACT_APP_JIRA_BASE_URL || '';

function ThinkingTimer({ startTime }) {
  const [elapsed, setElapsed] = React.useState(0);
  React.useEffect(() => {
    if (!startTime) return;
    const interval = setInterval(() => {
      setElapsed(Date.now() - startTime);
    }, 100);
    return () => clearInterval(interval);
  }, [startTime]);
  return `${(elapsed / 1000).toFixed(1)}s`;
}

export default function ChatWindow({ user, setUser: handleLogout }) {
  // Helper to get authorization header
  const getAuthHeader = () => {
    if (user.useSession && user.sessionToken) {
      return `Session ${user.sessionToken}`;
    }
    return `Bearer ${user.idToken}`;
  };
  
  const {
    messages = [],
    setMessages,
    loadDialogueMessages,
    pendingTransactions = [],
    setPendingTransactions,
    newIssueIds,
    setNewIssueIds,
    sendAudio: sendAudioToServer,
    commitTransactions: commitTransactionsToServer,
    cancelTransactions: cancelTransactionsToServer,
    retryUpload
  } = useRecording();

  const {
    currentDialogueId,
    setCurrentDialogueId,
    restoreDialogue,
    createNewDialogue,
    loadDialogues,
    creatingDialogue
  } = useDialogue();

  const [inputText, setInputText] = useState('');
  const [recordingState, setRecordingState] = useState({ isStarting: false, isRecording: false, isActive: false });
  const recordingActionRef = useRef('dictate'); // 'dictate' or 'send'
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAllApps, setShowAllApps] = useState(false);
  const [currentView, setCurrentView] = useState(() => {
    // Check URL hash to determine initial view
    const hash = window.location.hash.slice(1);
    if (hash && (hash.startsWith('my') || hash.startsWith('others') || hash.startsWith('recent') || hash.startsWith('board'))) {
      return 'jira';
    }
    return 'assistant';
  }); // 'assistant', 'jira', or 'fast-issues'
  const [returnToView, setReturnToView] = useState(null); // View to return to when closing issue full view
  const [processingTransaction, setProcessingTransaction] = useState(false);
  const [autocommit, setAutocommit] = useState(() => localStorage.getItem('autocommit') === 'true');
  const [autoresume, setAutoresume] = useState(() => localStorage.getItem('autoresumeChat') === 'true'); // Default false
  const [countdown, setCountdown] = useState(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showHints, setShowHints] = useState(true);
  const [contextMenu, setContextMenu] = useState(null);
  const [issueContextMenu, setIssueContextMenu] = useState(null); // { x, y, issueKey }
  const [reportingBug, setReportingBug] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [feedbackModal, setFeedbackModal] = useState(null); // { message, submitting, submitted, issueKey, error }
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [votedMessages, setVotedMessages] = useState({}); // { messageId: 'up' | 'down' }
  const [editingTransaction, setEditingTransaction] = useState(null); // { index, transaction }
  const [userLookup, setUserLookup] = useState({}); // jiraId -> displayName mapping
  const [fullscreenInput, setFullscreenInput] = useState(false); // Fullscreen text input modal
  const [inputLineCount, setInputLineCount] = useState(1); // Track line count for expand button
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight); // For fullscreen modal on mobile
  const [viewportOffset, setViewportOffset] = useState(0); // Visual viewport offset from top
  const [aiMode, setAiMode] = useState(() => localStorage.getItem('aiMode') || 'speed');
  const [shareTooltip, setShareTooltip] = useState(false);

  // Single instance of the recording hook
  const { start, stop, recording, starting, supported } = usePushToTalk();
  
  const threadRef = useRef(null);
  const socketRef = useRef(null);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const hintsScrollRef = useRef(null);
  const stopRecordingRef = useRef(null);
  const cancelRecordingRef = useRef(null);
  const hintsFadeTimeoutRef = useRef(null);
  const scrollPositionsRef = useRef(new Map()); // Store scroll positions per dialogue ID
  const prevDialogueIdRef = useRef(null); // Track previous dialogue to save scroll position
  const lastScrollTopRef = useRef(0); // Previous scrollTop for direction detection
  const lastUserScrollUpAtRef = useRef(0); // Timestamp of last upward user scroll
  const SCROLL_UP_LOCKOUT_MS = 50;
  const currentDialogueIdRef = useRef(currentDialogueId); // Always holds latest dialogueId for async checks
  currentDialogueIdRef.current = currentDialogueId;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('aiMode', aiMode);
  }, [aiMode]);

  // Fetch user lookup for converting account IDs to display names
  useEffect(() => {
    const fetchUserLookup = async () => {
      try {
        const res = await fetch('/api/jira/users/lookup', {
          headers: { Authorization: getAuthHeader() }
        });
        if (res.ok) {
          const data = await res.json();
          setUserLookup(data.lookup || {});
        }
      } catch (err) {
        console.error('Failed to fetch user lookup:', err);
      }
    };
    fetchUserLookup();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save/restore scroll position when dialogue changes
  useEffect(() => {
    const container = threadRef.current;

    // Save scroll position of previous dialogue before switching
    if (prevDialogueIdRef.current && container) {
      scrollPositionsRef.current.set(prevDialogueIdRef.current, container.scrollTop);
    }

    // Update previous dialogue ref
    prevDialogueIdRef.current = currentDialogueId;

    // Restore or scroll to bottom after a small delay (to let messages render)
    if (currentDialogueId && container) {
      setTimeout(() => {
        const savedPosition = scrollPositionsRef.current.get(currentDialogueId);
        if (savedPosition !== undefined) {
          // Restore saved position
          container.scrollTop = savedPosition;
        } else {
          // First time viewing this dialogue - scroll to bottom
          container.scrollTop = container.scrollHeight;
        }
      }, 50);
    }
  }, [currentDialogueId]);

  // Restore scroll position when returning to assistant view
  useEffect(() => {
    if (currentView === 'assistant' && currentDialogueId) {
      const container = threadRef.current;
      if (container) {
        setTimeout(() => {
          const savedPosition = scrollPositionsRef.current.get(currentDialogueId);
          if (savedPosition !== undefined) {
            container.scrollTop = savedPosition;
          } else {
            container.scrollTop = container.scrollHeight;
          }
        }, 50);
      }
    }
  }, [currentView, currentDialogueId]);

  // Track scroll position
  useEffect(() => {
    const container = threadRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const prevTop = lastScrollTopRef.current;
      lastScrollTopRef.current = scrollTop;

      const threshold = 20;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const atBottom = distanceFromBottom <= threshold
        || (scrollTop + clientHeight) >= (scrollHeight - 5);

      // Auto-follow is a sticky intent driven ONLY by the user: disable when
      // the user scrolls up, re-enable when the user scrolls back to the
      // bottom. Never disable based on position alone — during smooth
      // auto-scroll, scrollTop lags scrollHeight as new tokens arrive, and a
      // position-based check would flip the intent mid-stream.
      if (scrollTop < prevTop - 1) {
        lastUserScrollUpAtRef.current = Date.now();
        if (!atBottom) {
          setIsAtBottom(false);
          setShowHints(false);
          clearTimeout(hintsFadeTimeoutRef.current);
        }
        return;
      }

      if (atBottom) {
        setIsAtBottom(true);
        clearTimeout(hintsFadeTimeoutRef.current);
        hintsFadeTimeoutRef.current = setTimeout(() => {
          setShowHints(true);
        }, 1500);
      }
    };

    container.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(hintsFadeTimeoutRef.current);
    };
  }, []);

  // Handle countdown timer for autocommit will be added after handleTransactionConfirm is defined

  // Enable horizontal scroll with mouse wheel on desktop
  useEffect(() => {
    const hintsElement = hintsScrollRef.current;
    if (!hintsElement) return;

    const handleWheel = (e) => {
      if (e.deltaY !== 0) {
        e.preventDefault();
        hintsElement.scrollLeft += e.deltaY;
      }
    };

    hintsElement.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      hintsElement.removeEventListener('wheel', handleWheel);
    };
  }, [messages?.length]); // Re-attach when hints appear/disappear

  // Preflight mic permission ONCE (immediately release the stream)
  useEffect(() => {
    const preflight = async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true });
        s.getTracks().forEach(t => t.stop());
        console.log('Mic permission granted (preflight), stream released');
      } catch (err) {
        console.log('Mic permission denied or error:', err);
      }
    };
    preflight();
  }, []);

  // Restore last dialogue on mount (or create new if autoresume is off)
  useEffect(() => {
    const shouldAutoresume = localStorage.getItem('autoresumeChat') === 'true';

    const restoreLastDialogue = async () => {
      const lastDialogueId = localStorage.getItem('lastDialogueId');

      if (!shouldAutoresume) {
        // Autoresume is off - start fresh (but don't create dialogue until first message)
        console.log('[Dialogue] Autoresume disabled - starting fresh');
        setMessages([]);
        return;
      }

      if (lastDialogueId) {
        console.log(`[Dialogue] Restoring last dialogue: ${lastDialogueId}`);
        await restoreDialogue(lastDialogueId, (messages, transactions) => {
          // Convert Firestore messages to frontend format
          const formattedMessages = messages.map(msg => ({
            id: msg.id,
            sender: msg.role === 'user' ? 'me' : 'bot',
            text: msg.content,
            ts: msg.created_at
          }));

          loadDialogueMessages(formattedMessages, lastDialogueId);
          setPendingTransactions(transactions);
          setNewIssueIds(new Set());
        });
      }
    };

    restoreLastDialogue();
  }, [restoreDialogue, setMessages, setPendingTransactions, setNewIssueIds]);

  // Listen for hash changes to switch views
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash && (hash.startsWith('my') || hash.startsWith('others') || hash.startsWith('recent') || hash.startsWith('board'))) {
        setCurrentView('jira');
      } else if (!hash || hash === '') {
        // Empty hash or no hash - go to assistant
        setCurrentView('assistant');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const sendText = async () => {
    if (!inputText.trim()) return;

    const text = inputText.trim();
    setInputText('');

    const ts = new Date();
    const id = ts.getTime();
    const sentDialogueId = currentDialogueId; // Capture at send time
    
    setMessages(m => [...m, {
      id,
      sender: 'me',
      text,
      ts
    }]);
    
    setMessages(list => [
      ...list,
      {
        id: `${id}-bot`,
        sender: 'bot',
        parts: ['', '', ''],
        text: '',
        steps: [{ id: 'classify', label: 'Planning actions', status: 'active', startTime: Date.now() }],
        ts: new Date(),
      },
    ]);
    
    try {
      const bodyObj = { text, aiMode };
      const res = await fetch(`/api/message?id=${id}&dialogueId=${currentDialogueId || ''}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthHeader()
        },
        body: JSON.stringify(bodyObj)
      });

      if (res.status === 401) {
        const body = await res.json().catch(() => null);
        if (body?.error === 'GOOGLE_TOKEN_EXPIRED') {
          await new Promise(resolve => {
            window.google.accounts.id.initialize({
              client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
              login_hint: user.email,
              auto_select: true,
              callback: ({ credential }) => {
                if (credential) {
                  // Update just the token, not logout
                  // This would need to be handled by parent
                  window.location.reload();
                } else {
                  handleLogout();
                }
                resolve();
              },
            });
            window.google.accounts.id.prompt();
          });
          
          const retryRes = await fetch(`/api/message?id=${id}&dialogueId=${currentDialogueId || ''}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: getAuthHeader()
            },
            body: JSON.stringify(bodyObj)
          });
          const retryData = await retryRes.json();
          // Only set transactions if still in the same dialogue
          if (currentDialogueIdRef.current === sentDialogueId || currentDialogueIdRef.current === retryData.dialogueId) {
            if (retryData.transactions && retryData.transactions.length > 0) {
              setPendingTransactions(retryData.transactions);
            } else {
              setPendingTransactions([]);
            }
          } else {
            console.log(`[sendText] Dialogue changed during retry (was ${sentDialogueId}, now ${currentDialogueIdRef.current}). Skipping transaction update.`);
          }
        }
      } else {
        const data = await res.json();

        // If backend created a new dialogue, update current dialogue ID
        if (data.dialogueId && !currentDialogueId) {
          setCurrentDialogueId(data.dialogueId);
          localStorage.setItem('lastDialogueId', data.dialogueId);
          console.log(`[Dialogue] New dialogue created: ${data.dialogueId}`);
        }

        // Only set transactions if still in the same dialogue
        const nowDialogueId = currentDialogueIdRef.current;
        if (nowDialogueId === sentDialogueId || nowDialogueId === data.dialogueId) {
          if (data.transactions && data.transactions.length > 0) {
            setPendingTransactions(data.transactions);
            setNewIssueIds(new Set(data.newIssueIds || []));
          } else {
            setPendingTransactions([]);
            setNewIssueIds(new Set());
          }
        } else {
          console.log(`[sendText] Dialogue changed during request (was ${sentDialogueId}, now ${nowDialogueId}). Skipping transaction update.`);
        }
      }
    } catch (err) {
      console.error(err);
      setMessages(list => {
        const idx = list.findIndex(m => m.id === `${id}-bot`);
        if (idx === -1) return list;
        const copy = [...list];
        copy[idx] = {
          ...copy[idx],
          text: 'Sorry, there was an error processing your request.',
          parts: ['', '', 'Sorry, there was an error processing your request.']
        };
        return copy;
      });
    }
  };

  const sendAudio = async source => {
    // Use the shared sendAudio from context
    await sendAudioToServer(source);
  };

  // Dictation: transcribe audio and insert text into input field
  const [isDictating, setIsDictating] = useState(false);
  const dictateAudio = async (blob) => {
    if (!blob || !blob.size) return;

    setIsDictating(true);
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'dictation.webm');

      const response = await fetch('/api/dictate', {
        method: 'POST',
        headers: {
          Authorization: getAuthHeader()
        },
        body: formData
      });

      const data = await response.json();
      if (data.success && data.text) {
        // Append transcribed text to input (with space if there's existing text)
        setInputText(prev => {
          const separator = prev && !prev.endsWith(' ') && !prev.endsWith('\n') ? ' ' : '';
          return prev + separator + data.text;
        });

        // Focus and put cursor at end (resize handled by useEffect)
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
            const len = textareaRef.current.value.length;
            textareaRef.current.setSelectionRange(len, len);
          }
        }, 0);
      } else if (data.error) {
        console.error('[Dictation] Error:', data.error);
      }
    } catch (err) {
      console.error('[Dictation] Failed:', err);
    } finally {
      setIsDictating(false);
    }
  };

  // Handler for when recording stops - routes to dictate or send based on action
  const handleRecordingComplete = async (blob) => {
    const action = recordingActionRef.current;
    recordingActionRef.current = 'dictate'; // Reset to default

    if (action === 'send') {
      await sendAudio(blob);
    } else {
      await dictateAudio(blob);
    }
  };

  const handleRetryUpload = async (messageId) => {
    await retryUpload(messageId);
  };

  // Resize textarea when input changes (handles clearing, dictation, paste, etc.)
  useEffect(() => {
    if (textareaRef.current) {
      if (!inputText) {
        textareaRef.current.style.height = 'auto';
        setInputLineCount(1);
      } else {
        // Resize for programmatic text changes (like dictation)
        textareaRef.current.style.height = 'auto';
        const lineHeight = 24;
        const maxHeight = lineHeight * 8;
        const newHeight = Math.min(textareaRef.current.scrollHeight, maxHeight);
        textareaRef.current.style.height = newHeight + 'px';
        setInputLineCount(Math.ceil(textareaRef.current.scrollHeight / lineHeight));
      }
    }
  }, [inputText]);

  // Track viewport height and offset for fullscreen modal (handles mobile keyboard)
  useEffect(() => {
    if (!fullscreenInput) return;

    const updateViewport = () => {
      if (window.visualViewport) {
        setViewportHeight(window.visualViewport.height);
        setViewportOffset(window.visualViewport.offsetTop);
      } else {
        setViewportHeight(window.innerHeight);
        setViewportOffset(0);
      }
    };

    updateViewport();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewport);
      window.visualViewport.addEventListener('scroll', updateViewport);
      return () => {
        window.visualViewport.removeEventListener('resize', updateViewport);
        window.visualViewport.removeEventListener('scroll', updateViewport);
      };
    } else {
      window.addEventListener('resize', updateViewport);
      return () => window.removeEventListener('resize', updateViewport);
    }
  }, [fullscreenInput]);

  // Mask only new issue IDs, preserving the project prefix
  const maskNewIssueIds = (text, newIds) => {
    if (!text || !newIds || newIds.size === 0) return text;

    // Match any issue ID pattern (PROJECT-NUMBER)
    return text.replace(/\b([A-Z][A-Z0-9]*)-(\d+)\b/g, (match, prefix, number) => {
      if (newIds.has(match)) {
        // Preserve the prefix, mask only the number
        return `${prefix}-XXXXX`;
      }
      return match; // Keep existing IDs unchanged
    });
  };

  // Format dates from YYYY-MM-DD to DD Month YYYY
  const formatDates = (text) => {
    if (!text) return text;

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];

    // Match ISO date format YYYY-MM-DD
    return text.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (match, year, month, day) => {
      const monthIndex = parseInt(month, 10) - 1;
      const dayNum = parseInt(day, 10);

      if (monthIndex >= 0 && monthIndex < 12 && dayNum >= 1 && dayNum <= 31) {
        return `${dayNum} ${monthNames[monthIndex]} ${year}`;
      }
      return match; // Return original if invalid date
    });
  };

  // Create issue link element
  const createIssueLink = (issueKey, key) => {
    if (issueKey.endsWith('XXXXX')) {
      return <span key={key} className="masked-issue-id">{issueKey}</span>;
    }
    return (
      <a
        key={key}
        href={`#my/${issueKey}`}
        className="issue-link"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIssueContextMenu({
            x: e.clientX,
            y: e.clientY,
            issueKey: issueKey
          });
        }}
      >
        {issueKey}
      </a>
    );
  };

  // Check if text is an issue ID
  const isIssueId = (text) => {
    return /^[A-Z][A-Z0-9]{1,9}-(?:\d+|XXXXX)$/.test(text) &&
      !text.match(/^(GPT|UTF|ISO|IEEE|HTTP|HTTPS|API|CPU|GPU|RAM|ROM|PDF|PNG|JPG|JPEG|GIF|SVG|XML|JSON|HTML|CSS|SQL|SSH|FTP|SMTP|DNS|TCP|UDP|SHA|MD5|RSA|AES|DES|JWT|AWS|GCP)-\d+$/i);
  };

  // Convert issue IDs in a text string to links
  const convertIssueIds = (text, keyPrefix) => {
    if (!text || typeof text !== 'string') return text;

    // First parse markdown links [text](url)
    const linkParts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
    if (linkParts.length > 1) {
      return linkParts.map((part, i) => {
        const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (linkMatch) {
          return <a key={`${keyPrefix}-mdlink-${i}`} href={safeUrl(linkMatch[2])} target="_blank" rel="noopener noreferrer" style={{color: '#4a9eff', textDecoration: 'underline'}}>{linkMatch[1]}</a>;
        }
        // Recurse for issue IDs in non-link parts
        return convertIssueIdsOnly(part, `${keyPrefix}-${i}`);
      });
    }

    return convertIssueIdsOnly(text, keyPrefix);
  };

  const convertIssueIdsOnly = (text, keyPrefix) => {
    if (!text || typeof text !== 'string') return text;

    const parts = text.split(/\b([A-Z][A-Z0-9]{1,9}-(?:\d+|XXXXX))\b/g);
    if (parts.length === 1) return text;

    return parts.map((part, i) => {
      if (isIssueId(part)) {
        return createIssueLink(part, `${keyPrefix}-link-${i}`);
      }
      return part;
    });
  };

  // Parse markdown and convert issue IDs - processes whole text to keep ** pairs intact
  const addIssueLinks = (text, shouldMaskNew = false) => {
    if (!text) return text;

    // First mask new IDs and format dates
    let processedText = shouldMaskNew ? maskNewIssueIds(text, newIssueIds) : text;
    processedText = formatDates(processedText);

    // Parse bold first, then italic, then issue IDs within each text segment
    const result = [];
    let remaining = processedText;
    let partIndex = 0;

    while (remaining) {
      // Look for **bold**
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      if (boldMatch) {
        // Process text before bold
        const before = remaining.slice(0, boldMatch.index);
        if (before) {
          result.push(...parseItalicWithLinks(before, `p${partIndex++}`));
        }
        // Process bold content (may contain italic and links)
        result.push(
          <strong key={`b${partIndex++}`}>
            {parseItalicWithLinks(boldMatch[1], `bi${partIndex}`)}
          </strong>
        );
        remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
      } else {
        // No more bold, process remaining for italic and links
        result.push(...parseItalicWithLinks(remaining, `p${partIndex++}`));
        break;
      }
    }

    return result.length > 0 ? result : processedText;
  };

  // Parse italic and convert issue IDs
  const parseItalicWithLinks = (text, keyPrefix) => {
    if (!text) return [];

    const result = [];
    let remaining = text;
    let partIndex = 0;

    while (remaining) {
      // Match *text* but not **text**
      const italicMatch = remaining.match(/(?<!\*)\*([^*]+)\*(?!\*)/);
      if (italicMatch) {
        const before = remaining.slice(0, italicMatch.index);
        if (before) {
          const linked = convertIssueIds(before, `${keyPrefix}-${partIndex++}`);
          if (Array.isArray(linked)) result.push(...linked);
          else result.push(linked);
        }
        result.push(
          <em key={`${keyPrefix}-i${partIndex++}`}>
            {convertIssueIds(italicMatch[1], `${keyPrefix}-il${partIndex}`)}
          </em>
        );
        remaining = remaining.slice(italicMatch.index + italicMatch[0].length);
      } else {
        if (remaining) {
          const linked = convertIssueIds(remaining, `${keyPrefix}-${partIndex++}`);
          if (Array.isArray(linked)) result.push(...linked);
          else result.push(linked);
        }
        break;
      }
    }

    return result;
  };

  const handleHintClick = (hint) => {
    // Use the input text if it's an object, otherwise use the hint directly
    const textToSend = typeof hint === 'object' ? hint.input : hint;
    setInputText(textToSend);
    // Don't auto-send, let user see the text and press enter/send
    // Focus the textarea so user can just press enter
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Handle audio files
    if (file.type.startsWith('audio/') || file.type === 'video/mp4') {
      sendAudio(file);
      return;
    }

    // Handle text files (.txt, .md) — read content inline
    const fileName = file.name.toLowerCase();
    if (file.type === 'text/plain' || file.type === 'text/markdown' ||
        fileName.endsWith('.txt') || fileName.endsWith('.md')) {
      try {
        const text = await file.text();
        const prefix = `[File: ${file.name}]\n\n`;
        setInputText(prev => prev ? prev + '\n\n' + prefix + text : prefix + text);
      } catch (err) {
        alert('Failed to read text file');
      }
      return;
    }

    // Handle PDFs, images, and other files — upload to server and insert link
    if (file.type === 'application/pdf' || file.type.startsWith('image/') ||
        fileName.endsWith('.pdf') || fileName.match(/\.(png|jpg|jpeg|gif|webp)$/)) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch('/api/upload', {
          method: 'POST',
          headers: { Authorization: getAuthHeader() },
          body: formData,
        });
        if (!res.ok) throw new Error('Upload failed');
        const data = await res.json();
        const link = `[Attached: ${file.name}](${data.url})`;
        setInputText(prev => prev ? prev + '\n\n' + link : link);
      } catch (err) {
        alert('Failed to upload file: ' + err.message);
      }
      return;
    }

    alert('Unsupported file type. Please select audio, text, PDF, or image files.');
  };

  const handleTransactionConfirm = useCallback(async () => {
    if (processingTransaction) return;

    setProcessingTransaction(true);
    try {
      const dialogueId = currentDialogueId || localStorage.getItem('lastDialogueId');
      const res = await fetch('/api/transactions/commit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthHeader()
        },
        body: JSON.stringify({ dialogueId, transactions: pendingTransactions })
      });
      
      const data = await res.json();
      if (data.success) {
        setPendingTransactions([]);
        setNewIssueIds(new Set());

        // Trigger delta sync to refresh issues after Jira changes (with 2 second delay)
        // First ensure auth token is current on all caches
        const authToken = getAuthHeader();
        issueCacheSingleton.get('my').setAuthToken(authToken);
        issueCacheSingleton.get('others').setAuthToken(authToken);
        issueCacheSingleton.get('all').setAuthToken(authToken);

        issueCacheSingleton.triggerDeltaSync('my', 2000);
        issueCacheSingleton.triggerDeltaSync('others', 2000);
        issueCacheSingleton.triggerDeltaSync('all', 2000);

        // Refresh messages from server to get updated IDs
        // This handles the case when user left and returned to dialogue
        if (dialogueId) {
          try {
            const msgRes = await fetch(`/api/dialogues/${dialogueId}/messages`, {
              headers: { Authorization: getAuthHeader() }
            });
            if (msgRes.ok) {
              const msgData = await msgRes.json();
              if (msgData.success && msgData.messages) {
                const formattedMessages = msgData.messages.map(msg => ({
                  id: msg.id,
                  sender: msg.role === 'user' ? 'me' : 'bot',
                  text: msg.content,
                  ts: msg.created_at
                }));
                setMessages(formattedMessages);
              }
            }
          } catch (refreshErr) {
            console.error('Failed to refresh messages after commit:', refreshErr);
          }
        }

        // Also try local ID replacement as fallback
        if (data.results && data.results.idMap && Object.keys(data.results.idMap).length > 0) {
          const idMap = data.results.idMap;

          // Find the last user message to only update bot messages after it
          setMessages(list => {
            // Find index of last user message
            let lastUserMsgIndex = -1;
            for (let i = list.length - 1; i >= 0; i--) {
              if (list[i].sender === 'me') {
                lastUserMsgIndex = i;
                break;
              }
            }

            // Update only bot messages after the last user message
            return list.map((msg, index) => {
              if (msg.sender === 'bot' && index > lastUserMsgIndex) {
              let updated = false;
              let updatedMsg = { ...msg };

              // Update main text
              if (msg.text) {
                let updatedText = msg.text;
                for (const [fakeId, realId] of Object.entries(idMap)) {
                  // Replace various formats: [QUEUE-123456], QUEUE-123456, etc.
                  const newText = updatedText.replace(
                    new RegExp(`\\b${escapeRegExp(fakeId)}\\b`, 'g'),
                    realId
                  );
                  if (newText !== updatedText) {
                    updatedText = newText;
                    updated = true;
                  }
                }
                if (updated) {
                  updatedMsg.text = updatedText;
                  updatedMsg.idsReplaced = true; // Mark that IDs have been replaced
                }
              }

              // Update parts array if it exists (for multi-line messages)
              if (msg.parts && Array.isArray(msg.parts)) {
                const updatedParts = msg.parts.map(part => {
                  let updatedPart = part;
                  for (const [fakeId, realId] of Object.entries(idMap)) {
                    updatedPart = updatedPart.replace(
                      new RegExp(`\\b${escapeRegExp(fakeId)}\\b`, 'g'),
                      realId
                    );
                  }
                  return updatedPart;
                });

                // Check if any parts were updated
                if (JSON.stringify(updatedParts) !== JSON.stringify(msg.parts)) {
                  updatedMsg.parts = updatedParts;
                  updatedMsg.idsReplaced = true; // Mark that IDs have been replaced
                  updated = true;
                }
              }

              return updated ? { ...updatedMsg, idsReplaced: true } : msg;
            }
            return msg;
            });
          });

          // Add simple success message
          setMessages(list => [...list, {
            id: Date.now() + '-tx-success',
            sender: 'bot',
            text: '✅ Transactions committed successfully',
            ts: new Date()
          }]);
        } else {
          // Add simple success message if no ID mappings
          setMessages(list => [...list, {
            id: Date.now() + '-tx-success',
            sender: 'bot',
            text: '✅ Transactions committed successfully',
            ts: new Date()
          }]);
        }
      } else {
        // Handle failure - show detailed error message
        let errorMessage = '❌ Failed to commit transactions\n\n';
        
        if (data.errors && data.errors.length > 0) {
          errorMessage += 'Failed transactions:\n';
          data.errors.forEach((err, idx) => {
            errorMessage += `${idx + 1}. ${err.transaction}: ${err.error}\n`;
          });
        } else if (data.message) {
          errorMessage += data.message;
        } else {
          errorMessage += 'Unknown error occurred';
        }
        
        setMessages(list => [...list, {
          id: Date.now() + '-tx-error',
          sender: 'bot',
          text: errorMessage,
          ts: new Date()
        }]);
        
        // Clear transactions on failure (they're already cleared on backend)
        setPendingTransactions([]);
        setNewIssueIds(new Set());
      }
    } catch (err) {
      console.error('Failed to commit transactions:', err);
      setMessages(list => [...list, {
        id: Date.now() + '-tx-error',
        sender: 'bot',
        text: '❌ Failed to commit transactions: Network or server error',
        ts: new Date()
      }]);
    } finally {
      setProcessingTransaction(false);
    }
  }, [processingTransaction, user, pendingTransactions]);

  const handleTransactionCancel = async () => {
    if (processingTransaction) return;
    
    setProcessingTransaction(true);
    try {
      const dialogueId = currentDialogueId || localStorage.getItem('lastDialogueId');
      const res = await fetch('/api/transactions/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthHeader()
        },
        body: JSON.stringify({ dialogueId })
      });
      
      const data = await res.json();
      if (data.success) {
        // Get the fake IDs we need to mask before clearing
        const idsToMask = new Set(newIssueIds);

        setPendingTransactions([]);
        setNewIssueIds(new Set());

        // Trigger delta sync to refresh issues after cancelling (with 2 second delay)
        // First ensure auth token is current on all caches
        const authToken = getAuthHeader();
        issueCacheSingleton.get('my').setAuthToken(authToken);
        issueCacheSingleton.get('others').setAuthToken(authToken);
        issueCacheSingleton.get('all').setAuthToken(authToken);

        issueCacheSingleton.triggerDeltaSync('my', 2000);
        issueCacheSingleton.triggerDeltaSync('others', 2000);
        issueCacheSingleton.triggerDeltaSync('all', 2000);

        // Replace fake IDs with XXXXX in all messages
        if (idsToMask.size > 0) {
          setMessages(list => list.map(msg => {
            if (msg.sender === 'bot' && msg.text) {
              // Replace each fake ID with masked version
              let updatedText = msg.text;
              idsToMask.forEach(fakeId => {
                const [prefix] = fakeId.split('-');
                const maskedId = `${prefix}-XXXXX`;
                updatedText = updatedText.replace(new RegExp(`\\b${escapeRegExp(fakeId)}\\b`, 'g'), maskedId);
              });
              return { ...msg, text: updatedText, idsCancelled: true };
            }
            return msg;
          }));
        }
        
        // Add cancel message
        setMessages(list => [...list, {
          id: Date.now() + '-tx-cancel',
          sender: 'bot',
          text: '🚫 Transactions cancelled',
          ts: new Date()
        }]);
      }
    } catch (err) {
      console.error('Failed to cancel transactions:', err);
    } finally {
      setProcessingTransaction(false);
    }
  };

  // Handle countdown timer for autocommit
  useEffect(() => {
    if (!autocommit || pendingTransactions.length === 0 || processingTransaction) {
      setCountdown(null);
      return;
    }

    // Start countdown from 15
    setCountdown(15);
    
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          // Auto-commit when countdown reaches 0
          handleTransactionConfirm();
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [autocommit, pendingTransactions.length, processingTransaction, handleTransactionConfirm]);

  // Track previous message count to detect new messages
  const prevMessageCountRef = useRef(0);

  useEffect(() => {
    const messagesArea = threadRef.current;
    if (!messagesArea) return;

    const isNewMessage = messages.length > prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;

    // Auto-scroll when:
    //  - currently parked at the bottom AND user hasn't just scrolled up, OR
    //  - first paint of a dialogue with no saved position
    // Fires on every messages-array reference change, including per-token
    // streaming updates — CSS scroll-behavior: smooth animates it, and each new
    // assignment re-targets the animation so it never lags behind the text.
    const savedPosition = scrollPositionsRef.current.get(currentDialogueId);
    const initialLoad = isNewMessage && savedPosition === undefined;
    const recentlyScrolledUp = (Date.now() - lastUserScrollUpAtRef.current) < SCROLL_UP_LOCKOUT_MS;
    const shouldAutoScroll = (isAtBottom && !recentlyScrolledUp) || initialLoad;

    if (shouldAutoScroll) {
      // Instant snap, not the CSS smooth-scroll. Smooth animations interfere
      // with rapid re-targeting during streaming (the second scrollTop write
      // arrives while the first animation is mid-flight and gets dropped),
      // which made auto-scroll stall mid-message. Tokens arrive in small
      // increments so back-to-back instant snaps already look smooth.
      messagesArea.scrollTo({ top: messagesArea.scrollHeight, behavior: 'instant' });
      lastScrollTopRef.current = messagesArea.scrollHeight;
    }
  }, [messages, currentDialogueId, isAtBottom]);

  const handleScrollToBottom = () => {
    const container = threadRef.current;
    if (!container) return;
    // Forget any recent scroll-up so streaming auto-follow resumes immediately.
    lastUserScrollUpAtRef.current = 0;
    // Jump instantly, bypassing the CSS scroll-behavior: smooth on .messages-area.
    const prev = container.style.scrollBehavior;
    container.style.scrollBehavior = 'auto';
    container.scrollTop = container.scrollHeight;
    container.style.scrollBehavior = prev;
    lastScrollTopRef.current = container.scrollHeight;
    setIsAtBottom(true);
  };

  // Socket connection is now managed by RecordingContext
  // No need for duplicate socket here

  return (
    <>
      <div className="header-bar">
        <div className="header-left">
          <button className="menu-button" onClick={() => setMenuOpen(true)}>
            <div className="menu-icon">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
          <div className="app-title">
            {currentView === 'jira' ? 'Jira' : currentView === 'fast-issues' ? 'Fast Issues' : 'Assistant'}
          </div>
        </div>
        <div className="header-right">
          {currentView === 'assistant' && (
            <div className="ai-mode-toggle">
              <button
                className={`ai-mode-option${aiMode === 'speed' ? ' active' : ''}`}
                onClick={() => setAiMode('speed')}
              >Speed</button>
              <button
                className={`ai-mode-option${aiMode === 'quality' ? ' active' : ''}`}
                onClick={() => setAiMode('quality')}
              >Reasoning</button>
            </div>
          )}
          {currentView === 'assistant' && <InsightsBadge />}
          {currentView === 'assistant' && currentDialogueId && messages.length > 0 && (
            <div className="share-button-wrapper">
              <button
                className="share-button"
                title="Share thread"
                onClick={async () => {
                  try {
                    const res = await fetch(`/api/dialogues/${currentDialogueId}/share`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: getAuthHeader()
                      }
                    });
                    const data = await res.json();
                    if (data.success && data.shareToken) {
                      const url = `${window.location.origin}/share/${data.shareToken}`;
                      await navigator.clipboard.writeText(url);
                      setShareTooltip(true);
                      setTimeout(() => setShareTooltip(false), 2000);
                    }
                  } catch (err) {
                    console.error('Share failed:', err);
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
              </button>
              {shareTooltip && <span className="share-tooltip">Copied!</span>}
            </div>
          )}
        </div>
      </div>

      <div className="chat-container">
        {currentView === 'assistant' ? (
          <>
            <div className="messages-area" ref={threadRef}>
              <ul className="thread">
                {/* Default hints removed */}

                {messages.map((m, idx) => {
                  // Find if this is the last bot message
                  const isLastBotMessage = m.sender === 'bot' &&
                    !messages.slice(idx + 1).some(msg => msg.sender === 'bot');

                  return (
                    <li key={m.id} className={`${m.sender} ${m.uploadFailed ? 'upload-failed' : ''}`}>
                      <small
                        className="message-time"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedMessage(m);
                          setContextMenu({
                            x: e.clientX,
                            y: e.clientY
                          });
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        {new Date(m.ts).toLocaleTimeString()}
                      </small>
                      {m.audioUrl ? (
                        <>
                          <audio controls src={m.audioUrl} />
                          {m.uploadFailed && (
                            <>
                              <div className="upload-error-bar">
                                <span className="error-text">Uploading failed</span>
                                <button
                                  className="retry-upload-button"
                                  onClick={() => handleRetryUpload(m.id)}
                                >
                                  Retry upload
                                </button>
                              </div>
                              {m.errorMessage && (
                                <div className="error-message-text">{m.errorMessage}</div>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <span className="bubble">
                          {m.steps && m.steps.length > 0 && !m.parts?.[2] ? (
                            <div className="thinking-steps">
                              {m.parts?.[0] && <div className="thinking-transcript">{m.parts[0]}</div>}
                              {m.steps.map((step, i) => (
                                <div key={i} className={`thinking-step thinking-step--${step.status}`}>
                                  <span className="thinking-step-icon">
                                    {step.status === 'active' ? (
                                      <span className="thinking-spinner" />
                                    ) : step.status === 'done' ? (
                                      <span className="thinking-check">&#10003;</span>
                                    ) : (
                                      <span className="thinking-fail">&#10005;</span>
                                    )}
                                  </span>
                                  <span className="thinking-step-label">{step.label}</span>
                                  <span className="thinking-step-time">
                                    {step.status === 'active' ? (
                                      <ThinkingTimer startTime={step.startTime} />
                                    ) : step.elapsedMs != null ? (
                                      `${(step.elapsedMs / 1000).toFixed(1)}s`
                                    ) : ''}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : m.steps && m.steps.length > 0 && m.parts?.[2] ? (
                            <>
                              <details className="thinking-details">
                                <summary className="thinking-summary">
                                  {m.totalElapsedMs != null
                                    ? <>Completed in {(m.totalElapsedMs / 1000).toFixed(1)}s</>
                                    : (m.steps?.[0]?.startTime
                                        ? <>Working <ThinkingTimer startTime={m.steps[0].startTime} /></>
                                        : 'Working\u2026')}
                                </summary>
                                <div className="thinking-steps thinking-steps--collapsed">
                                  {m.steps.map((step, i) => (
                                    <div key={i} className={`thinking-step thinking-step--${step.status}`}>
                                      <span className="thinking-step-icon">
                                        {step.status === 'done' ? (
                                          <span className="thinking-check">&#10003;</span>
                                        ) : (
                                          <span className="thinking-fail">&#10005;</span>
                                        )}
                                      </span>
                                      <span className="thinking-step-label">{step.label}</span>
                                      <span className="thinking-step-time">
                                        {step.elapsedMs != null ? `${(step.elapsedMs / 1000).toFixed(1)}s` : ''}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </details>
                              <div className="thinking-final-text">
                                {(() => {
                                  const displayText = m.text || m.parts?.[2] || '';
                                  const shouldMask = isLastBotMessage && pendingTransactions.length > 0 && !m.idsReplaced;
                                  return addIssueLinks(displayText, shouldMask);
                                })()}
                              </div>
                            </>
                          ) : (
                            (() => {
                              const displayText = m.text || m.parts?.[2] || '';
                              const shouldMask = isLastBotMessage && pendingTransactions.length > 0 && !m.idsReplaced;
                              return addIssueLinks(displayText, shouldMask);
                            })()
                          )}
                        </span>
                      )}
                      {m.sender === 'bot' && m.parts?.[2] && !(m.steps?.some(s => s.status === 'active')) && (
                        <div className="feedback-buttons">
                          <button
                            className={`feedback-btn ${votedMessages[m.id] === 'up' ? 'voted' : ''}`}
                            onClick={async (e) => {
                              e.stopPropagation();

                              // Find the previous user message
                              let userInput = null;
                              const msgIndex = messages.findIndex(msg => msg.id === m.id);
                              for (let i = msgIndex - 1; i >= 0; i--) {
                                if (messages[i].sender === 'me') {
                                  userInput = messages[i].text;
                                  break;
                                }
                              }

                              try {
                                await fetch('/api/feedback/vote', {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: getAuthHeader()
                                  },
                                  body: JSON.stringify({
                                    vote: 'up',
                                    assistantResponse: m.text,
                                    userInput,
                                    messageId: m.id
                                  })
                                });
                                setVotedMessages(prev => ({ ...prev, [m.id]: 'up' }));
                              } catch (err) {
                                console.error('Failed to submit vote:', err);
                              }
                            }}
                            title="Good response"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
                            </svg>
                          </button>
                          <button
                            className={`feedback-btn ${votedMessages[m.id] === 'down' ? 'voted' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFeedbackModal({ message: m });
                            }}
                            title="Report issue with this response"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
                            </svg>
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
                
                {/* Transaction confirmation UI - inside messages area */}
                {pendingTransactions.length > 0 && (
                  <li className="transaction-item">
                    <div className="transaction-box">
                      <div className="transaction-text">
                        {pendingTransactions.length} pending transaction{pendingTransactions.length > 1 ? 's' : ''}
                        {autocommit && countdown !== null && (
                          <span className="countdown-text"> - Auto-commit in {countdown}s</span>
                        )}
                      </div>
                      <div className="transaction-buttons">
                        {!autocommit ? (
                          <>
                            {pendingTransactions.some(t => t.name === 'jira-create-issue') && (
                              <button
                                className="transaction-edit-btn"
                                onClick={() => {
                                  const idx = pendingTransactions.findIndex(t => t.name === 'jira-create-issue');
                                  if (idx >= 0) {
                                    setEditingTransaction({ index: idx, transaction: JSON.parse(JSON.stringify(pendingTransactions[idx])) });
                                  }
                                }}
                                title="Edit before committing"
                              >
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                  <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                  <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                </svg>
                              </button>
                            )}
                            <button
                              className="transaction-confirm"
                              onClick={handleTransactionConfirm}
                              disabled={processingTransaction}
                            >
                              {processingTransaction ? 'Processing...' : 'Confirm'}
                            </button>
                            <button
                              className="transaction-cancel"
                              onClick={handleTransactionCancel}
                              disabled={processingTransaction}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            {pendingTransactions.some(t => t.name === 'jira-create-issue') && (
                              <button
                                className="transaction-edit-btn"
                                onClick={() => {
                                  setCountdown(null);
                                  const idx = pendingTransactions.findIndex(t => t.name === 'jira-create-issue');
                                  if (idx >= 0) {
                                    setEditingTransaction({ index: idx, transaction: JSON.parse(JSON.stringify(pendingTransactions[idx])) });
                                  }
                                }}
                                title="Edit before committing"
                              >
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                  <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                  <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                </svg>
                              </button>
                            )}
                            <button
                              className="transaction-confirm"
                              onClick={() => {
                                setCountdown(null);
                                handleTransactionConfirm();
                              }}
                              disabled={processingTransaction}
                            >
                              {processingTransaction ? 'Processing...' : 'Commit'}
                            </button>
                            <button
                              className="transaction-cancel"
                              onClick={() => {
                                setCountdown(null);
                                handleTransactionCancel();
                              }}
                              disabled={processingTransaction}
                            >
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                )}

                <li ref={bottomRef} style={{ height: "1px" }} />
              </ul>
            </div>

            {showHints && isAtBottom && (
              <div className={`hints-container ${showHints ? 'fade-in' : ''}`}>
                <div className="hints-scroll" ref={hintsScrollRef}>
                  {HINT_MESSAGES.map((hint, index) => (
                    <button
                      key={index}
                      className="hint-chip"
                      onClick={() => handleHintClick(hint)}
                    >
                      {typeof hint === 'object' ? hint.display : hint}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!isAtBottom && (
              <button
                className="scroll-to-bottom-btn"
                onClick={handleScrollToBottom}
                aria-label="Scroll to bottom"
                title="Scroll to bottom"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <polyline points="19 12 12 19 5 12"/>
                </svg>
              </button>
            )}
          </>
        ) : currentView === 'jira' ? (
          <ErrorBoundary>
            <JiraDashboard
              user={user}
              returnToView={returnToView}
              onReturnToView={(view) => {
                setReturnToView(null);
                setCurrentView(view);
              }}
            />
          </ErrorBoundary>
        ) : currentView === 'fast-issues' ? (
          <ErrorBoundary>
            <FastIssuesView user={user} />
          </ErrorBoundary>
        ) : currentView === 'settings' ? (
          <Settings
            theme={theme}
            setTheme={setTheme}
            autocommit={autocommit}
            setAutocommit={setAutocommit}
            autoresume={autoresume}
            setAutoresume={setAutoresume}
            onBack={() => setCurrentView('assistant')}
            onAdvancedSettings={() => setCurrentView('advanced-settings')}
          />
        ) : currentView === 'advanced-settings' ? (
          <AdvancedSettings
            onBack={() => setCurrentView('settings')}
          />
        ) : null}

        {currentView === 'assistant' && (
          <div className="input-container">
            {inputLineCount > 4 && !recordingState.isActive && (
              <button
                className="expand-input-btn"
                onClick={() => setFullscreenInput(true)}
                title="Expand to fullscreen"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                </svg>
              </button>
            )}
          <div className="input-wrapper">
            {!recordingState.isActive ? (
              <>
                <button 
                  className="attach-button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={pendingTransactions.length > 0}
                >
                  +
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/*,.txt,.md,text/plain,text/markdown,.pdf,application/pdf,image/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </>
            ) : (
              <button 
                className="cancel-button"
                onClick={() => {
                  console.log('Cancel button clicked');
                  if (cancelRecordingRef.current) {
                    cancelRecordingRef.current();
                  }
                }}
                title="Cancel recording"
              >
                ✕
              </button>
            )}
            
            {!recordingState.isActive ? (
              <>
                <div className="text-input-container">
                  <textarea
                    ref={textareaRef}
                    className="text-input"
                    placeholder={isDictating ? "Transcribing..." : pendingTransactions.length > 0 ? "Please handle pending transactions first" : "Ask Anything"}
                    value={inputText}
                    onChange={(e) => {
                      setInputText(e.target.value);
                      // Auto-resize textarea
                      const textarea = e.target;
                      textarea.style.height = 'auto';
                      const lineHeight = 24; // Approximate line height
                      const maxLines = 8;
                      const maxHeight = lineHeight * maxLines;
                      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
                      textarea.style.height = newHeight + 'px';
                      // Calculate line count
                      const lines = Math.ceil(textarea.scrollHeight / lineHeight);
                      setInputLineCount(lines);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendText();
                      }
                    }}
                    disabled={pendingTransactions.length > 0}
                    rows={1}
                  />
                </div>
                {isDictating ? (
                  <div className="dictating-indicator">
                    <div className="dictating-spinner"></div>
                  </div>
                ) : (
                  <Recorder
                    onBlobReady={dictateAudio}
                    onRecordingChange={setRecordingState}
                    start={start}
                    stop={stop}
                    recording={recording}
                    starting={starting}
                    supported={supported && pendingTransactions.length === 0}
                    onStopClick={stopRecordingRef}
                    onCancelClick={cancelRecordingRef}
                  />
                )}
                <button
                  className={`send-button ${inputText.trim() ? 'active' : ''}`}
                  onClick={sendText}
                  disabled={!inputText.trim() || pendingTransactions.length > 0 || isDictating}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="icon">
                    <path d="M8.99992 16V6.41407L5.70696 9.70704C5.31643 10.0976 4.68342 10.0976 4.29289 9.70704C3.90237 9.31652 3.90237 8.6835 4.29289 8.29298L9.29289 3.29298L9.36907 3.22462C9.76184 2.90427 10.3408 2.92686 10.707 3.29298L15.707 8.29298L15.7753 8.36915C16.0957 8.76192 16.0731 9.34092 15.707 9.70704C15.3408 10.0732 14.7618 10.0958 14.3691 9.7754L14.2929 9.70704L10.9999 6.41407V16C10.9999 16.5523 10.5522 17 9.99992 17C9.44764 17 8.99992 16.5523 8.99992 16Z"></path>
                  </svg>
                </button>
              </>
            ) : (
              <div className="recording-container">
                <div className={`waveform ${recordingState.isStarting ? 'starting' : ''}`}>
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                </div>
                <Recorder
                  onBlobReady={handleRecordingComplete}
                  onRecordingChange={setRecordingState}
                  start={start}
                  stop={stop}
                  recording={recording}
                  starting={starting}
                  supported={supported && pendingTransactions.length === 0}
                  onStopClick={stopRecordingRef}
                  onCancelClick={cancelRecordingRef}
                />
                <button
                  className={`send-button ${recordingState.isRecording ? 'active' : 'disabled'}`}
                  onClick={() => {
                    if (stopRecordingRef.current) {
                      recordingActionRef.current = 'send';
                      stopRecordingRef.current();
                    }
                  }}
                  disabled={recordingState.isStarting}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="icon">
                    <path d="M8.99992 16V6.41407L5.70696 9.70704C5.31643 10.0976 4.68342 10.0976 4.29289 9.70704C3.90237 9.31652 3.90237 8.6835 4.29289 8.29298L9.29289 3.29298L9.36907 3.22462C9.76184 2.90427 10.3408 2.92686 10.707 3.29298L15.707 8.29298L15.7753 8.36915C16.0957 8.76192 16.0731 9.34092 15.707 9.70704C15.3408 10.0732 14.7618 10.0958 14.3691 9.7754L14.2929 9.70704L10.9999 6.41407V16C10.9999 16.5523 10.5522 17 9.99992 17C9.44764 17 8.99992 16.5523 8.99992 16Z"></path>
                  </svg>
                </button>
              </div>
            )}
          </div>
          </div>
        )}
      </div>

      <div className={`side-menu ${menuOpen ? 'open' : ''}`}>
        <div className="menu-header">
          {isIosApp ? (
            <button className="menu-title" onClick={() => { setMenuOpen(false); setShowAllApps(true); }} style={{background:'none',border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:'6px',padding:0,color:'inherit',fontSize:'inherit',fontWeight:'inherit'}}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{opacity:0.7}}>
                <rect x="1" y="1" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                <rect x="12" y="1" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                <rect x="1" y="12" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                <rect x="12" y="12" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
              All Apps
            </button>
          ) : (
            <div className="menu-title">Menu</div>
          )}
          <button className="menu-close" onClick={() => setMenuOpen(false)}>
            ✕
          </button>
        </div>
        <div className="menu-content">
          <div>
            <button
              className={`menu-button-item ${currentView === 'assistant' ? 'active' : ''}`}
              onClick={() => {
                window.location.hash = '';
                setCurrentView('assistant');
                setMenuOpen(false);
              }}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <span>Assistant</span>
              {currentView === 'assistant' && (
                <button
                  className="dialogue-new-icon-button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    // Clear current messages and transactions
                    setMessages([]);
                    setPendingTransactions([]);
                    setNewIssueIds(new Set());

                    // Create new dialogue
                    await createNewDialogue();

                    // Reload dialogue list
                    await loadDialogues();
                  }}
                  title="Start new dialogue"
                  disabled={creatingDialogue}
                >
                  +
                </button>
              )}
            </button>

            {currentView === 'assistant' && (
              <DialogueMenu onClose={() => setMenuOpen(false)} />
            )}

            <button
              className={`menu-button-item ${currentView === 'jira' ? 'active' : ''}`}
              onClick={() => {
                window.location.hash = '#my';
                setReturnToView(null); // Clear return view since this is intentional navigation
                setCurrentView('jira');
                setMenuOpen(false);
              }}
            >
              Jira
            </button>
          </div>
        </div>

        <div className="menu-footer">
          <div className="user-info">
            <div className="user-info-left">
              <div className="user-name">{user?.name || 'User'}</div>
              <button className="logout-button" onClick={() => {
                setMenuOpen(false);
                handleLogout();
              }}>
                Logout
              </button>
            </div>
            <button
              className="settings-button"
              onClick={() => {
                setMenuOpen(false);
                setCurrentView('settings');
              }}
              title="Settings"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      <div 
        className={`menu-overlay ${menuOpen ? 'open' : ''}`}
        onClick={() => setMenuOpen(false)}
      />
      
      {/* Context Menu */}
      {contextMenu && (
        <>
          <div
            className="context-menu-overlay"
            onClick={() => setContextMenu(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
          />
          <div
            className="context-menu"
            style={{
              position: 'fixed',
              left: contextMenu.x,
              top: contextMenu.y,
              background: '#fff',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              zIndex: 1000
            }}
          >
            <button
              onClick={() => {
                setContextMenu(null);
                setReportingBug(true);
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px 12px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                borderRadius: '4px',
                color: 'var(--text-color)'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--hover-bg)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              Report a bug
            </button>
          </div>
        </>
      )}

      {/* Issue Link Context Menu */}
      {issueContextMenu && (
        <>
          <div
            className="context-menu-overlay"
            onClick={() => setIssueContextMenu(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
          />
          <div
            className="issue-context-menu"
            style={{
              position: 'fixed',
              left: Math.min(issueContextMenu.x, window.innerWidth - 180),
              top: Math.min(issueContextMenu.y, window.innerHeight - 100),
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '4px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              minWidth: '160px'
            }}
          >
            <button
              onClick={() => {
                setIssueContextMenu(null);
                // Track current view to return to when closing issue
                if (currentView !== 'jira') {
                  setReturnToView(currentView);
                }
                setCurrentView('jira');
                window.location.hash = `my/${issueContextMenu.issueKey}`;
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '10px 12px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <span style={{ fontSize: '16px' }}>📱</span>
              Open
            </button>
            <button
              onClick={() => {
                setIssueContextMenu(null);
                window.open(`${JIRA_BASE_URL}/${issueContextMenu.issueKey}`, '_blank');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '10px 12px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <span style={{ fontSize: '16px' }}>🔗</span>
              Open in Jira
            </button>
          </div>
        </>
      )}

      {/* Bug Report Modal */}
      {reportingBug && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001
          }}
        >
          <div
            className="modal-content"
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '20px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '80vh',
              overflow: 'auto',
              border: '1px solid #ddd',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            <h3 style={{ color: '#000', marginBottom: '12px' }}>Report a Bug</h3>
            <p style={{ fontSize: '14px', opacity: 0.8, marginBottom: '16px', color: '#555' }}>
              This will submit the conversation context for the selected message.
            </p>
            <textarea
              placeholder="Describe the issue..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                background: '#f9f9f9',
                color: '#000',
                resize: 'vertical',
                marginBottom: '16px',
                fontFamily: 'inherit'
              }}
              id="bug-description"
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setReportingBug(false)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: '#000'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const description = document.getElementById('bug-description').value;
                  if (!description.trim()) {
                    alert('Please describe the issue');
                    return;
                  }
                  
                  try {
                    // Find the previous user message before the selected bot message
                    let userInput = null;
                    const msgIndex = messages.findIndex(m => m.id === selectedMessage.id);
                    for (let i = msgIndex - 1; i >= 0; i--) {
                      if (messages[i].sender === 'me') {
                        userInput = messages[i].text;
                        break;
                      }
                    }
                    
                    const report = {
                      description,
                      timestamp: new Date().toISOString(),
                      user: user.email,
                      dialogueId: currentDialogueId,
                      message: {
                        id: selectedMessage.backendMessageId || selectedMessage.id,  // Use backend ID if available
                        text: selectedMessage.text,
                        sender: selectedMessage.sender,
                        timestamp: selectedMessage.ts
                      },
                      userInput,
                      // Tool calls will be added from backend
                    };
                    
                    const response = await fetch('/api/report-bug', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: getAuthHeader()
                      },
                      body: JSON.stringify(report)
                    });
                    
                    if (response.ok) {
                      alert('Bug report submitted successfully');
                      setReportingBug(false);
                      setSelectedMessage(null);
                    } else {
                      alert('Failed to submit bug report');
                    }
                  } catch (error) {
                    console.error('Failed to submit bug report:', error);
                    alert('Error submitting bug report');
                  }
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#007bff',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {feedbackModal && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001
          }}
        >
          <div
            className="modal-content feedback-modal"
            style={{
              background: 'var(--bg-secondary, #ffffff)',
              borderRadius: '12px',
              padding: '20px',
              width: '90%',
              maxWidth: '400px',
              border: '1px solid var(--border-color, #ddd)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            {feedbackModal.submitted ? (
              // Success state
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ color: 'var(--text-color)', marginBottom: '8px' }}>Feedback Submitted</h3>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  Thank you! A Jira issue has been created:
                </p>
                <a
                  href={`${JIRA_BASE_URL}/${feedbackModal.issueKey}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    padding: '8px 16px',
                    background: 'var(--accent-color, #007bff)',
                    color: 'white',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    marginBottom: '16px'
                  }}
                >
                  {feedbackModal.issueKey}
                </a>
                <div>
                  <button
                    onClick={() => setFeedbackModal(null)}
                    style={{
                      padding: '8px 24px',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)',
                      background: 'transparent',
                      cursor: 'pointer',
                      color: 'var(--text-color)'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : feedbackModal.error ? (
              // Error state
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ color: 'var(--text-color)', marginBottom: '8px' }}>Submission Failed</h3>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  {feedbackModal.error}
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button
                    onClick={() => setFeedbackModal({ message: feedbackModal.message })}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      background: 'var(--accent-color, #007bff)',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => setFeedbackModal(null)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)',
                      background: 'transparent',
                      cursor: 'pointer',
                      color: 'var(--text-color)'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              // Input state
              <>
                <h3 style={{ color: 'var(--text-color)', marginBottom: '12px' }}>
                  Report Issue
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  What went wrong with this response?
                </p>
                <textarea
                  placeholder="Describe the issue..."
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-primary, #f9f9f9)',
                    color: 'var(--text-color)',
                    resize: 'vertical',
                    marginBottom: '16px',
                    fontFamily: 'inherit',
                    fontSize: '14px'
                  }}
                  id="feedback-description"
                  autoFocus
                />
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={async () => {
                      const description = document.getElementById('feedback-description').value;
                      if (!description.trim()) {
                        alert('Please describe the issue');
                        return;
                      }

                      setSubmittingFeedback(true);

                      try {
                        // Find the previous user message
                        let userInput = null;
                        const msgIndex = messages.findIndex(m => m.id === feedbackModal.message.id);
                        for (let i = msgIndex - 1; i >= 0; i--) {
                          if (messages[i].sender === 'me') {
                            userInput = messages[i].text;
                            break;
                          }
                        }

                        const response = await fetch('/api/feedback', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: getAuthHeader()
                          },
                          body: JSON.stringify({
                            description,
                            assistantResponse: feedbackModal.message.text,
                            userInput,
                            messageId: feedbackModal.message.id
                          })
                        });

                        const data = await response.json();

                        if (response.ok && data.success) {
                          setVotedMessages(prev => ({ ...prev, [feedbackModal.message.id]: 'down' }));
                          setFeedbackModal({
                            ...feedbackModal,
                            submitted: true,
                            issueKey: data.issueKey
                          });
                        } else {
                          setFeedbackModal({
                            ...feedbackModal,
                            error: data.error || 'Failed to submit feedback'
                          });
                        }
                      } catch (error) {
                        console.error('Failed to submit feedback:', error);
                        setFeedbackModal({
                          ...feedbackModal,
                          error: 'Network error. Please try again.'
                        });
                      } finally {
                        setSubmittingFeedback(false);
                      }
                    }}
                    disabled={submittingFeedback}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      background: 'var(--accent-color, #007bff)',
                      color: 'white',
                      cursor: submittingFeedback ? 'not-allowed' : 'pointer',
                      opacity: submittingFeedback ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {submittingFeedback ? 'Submitting...' : 'Submit'}
                  </button>
                  <button
                    onClick={() => setFeedbackModal(null)}
                    disabled={submittingFeedback}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)',
                      background: 'transparent',
                      cursor: submittingFeedback ? 'not-allowed' : 'pointer',
                      color: 'var(--text-color)',
                      opacity: submittingFeedback ? 0.5 : 1
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit All Transactions Modal */}
      {editingTransaction && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001
          }}
        >
          <div
            className="modal-content edit-transaction-modal"
            style={{
              background: 'var(--bg-secondary, #ffffff)',
              borderRadius: '12px',
              padding: '20px',
              width: '95%',
              maxWidth: '700px',
              maxHeight: '85vh',
              overflow: 'auto',
              border: '1px solid var(--border-color, #ddd)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            <h3 style={{ color: 'var(--text-color)', marginBottom: '16px' }}>
              Edit Transactions ({pendingTransactions.length})
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {pendingTransactions.map((tx, idx) => (
                <div
                  key={idx}
                  style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '12px',
                    background: 'var(--bg-primary)'
                  }}
                >
                  <div style={{
                    fontWeight: 'bold',
                    color: 'var(--accent-color)',
                    marginBottom: '12px',
                    fontSize: '14px'
                  }}>
                    {idx + 1}. {tx.name}
                  </div>

                  {/* Editable fields for create-issue */}
                  {tx.name === 'jira-create-issue' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Summary</label>
                        <input
                          type="text"
                          value={tx.args?.summary || ''}
                          onChange={(e) => {
                            const newTx = [...pendingTransactions];
                            newTx[idx] = { ...tx, args: { ...tx.args, summary: e.target.value } };
                            setPendingTransactions(newTx);
                          }}
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-color)', fontSize: '13px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Description</label>
                        <textarea
                          value={tx.args?.description || ''}
                          onChange={(e) => {
                            const newTx = [...pendingTransactions];
                            newTx[idx] = { ...tx, args: { ...tx.args, description: e.target.value } };
                            setPendingTransactions(newTx);
                          }}
                          rows={3}
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-color)', fontSize: '13px', resize: 'vertical' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Due Date</label>
                        <input
                          type="date"
                          value={tx.args?.dueDateISO?.split('T')[0] || ''}
                          onChange={(e) => {
                            const newTx = [...pendingTransactions];
                            newTx[idx] = { ...tx, args: { ...tx.args, dueDateISO: e.target.value ? e.target.value + 'T00:00:00.000Z' : '' } };
                            setPendingTransactions(newTx);
                          }}
                          style={{ padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-color)', fontSize: '13px' }}
                        />
                      </div>

                      {/* Show other fields (non-editable) */}
                      {(() => {
                        const editableFields = ['summary', 'description', 'dueDateISO'];
                        const hiddenFields = ['sys_token', 'user_account_id', 'dialogueId', 'user', 'issue_key', 'duedate'];

                        // Helper to convert account ID to display name
                        const toDisplayName = (accountId) => {
                          if (!accountId) return null;
                          return userLookup[accountId] || accountId;
                        };

                        // Collect fields from tx.args, filtering nulls
                        let otherArgs = Object.entries(tx.args || {}).filter(
                          ([key, value]) => !editableFields.includes(key) && !hiddenFields.includes(key) && value !== null && value !== undefined
                        );

                        // Also get fields from dryRunResult.params (where consolidated fields like need-to-know go)
                        const dryRunParams = tx.dryRunResult?.params || {};
                        const dryRunFields = dryRunParams.fields || {};

                        // Merge dryRunResult.params fields that aren't already in args
                        const argsKeys = new Set(otherArgs.map(([k]) => k));
                        Object.entries(dryRunParams).forEach(([key, value]) => {
                          if (!editableFields.includes(key) && !hiddenFields.includes(key) && !argsKeys.has(key) && key !== 'fields' && value !== null && value !== undefined) {
                            otherArgs.push([key, value]);
                          }
                        });

                        // Add custom fields from dryRunResult.params.fields (like e.g. the need-to-know field)
                        // But filter out duedate and assignee (already shown elsewhere)
                        const filteredDryRunFields = Object.fromEntries(
                          Object.entries(dryRunFields).filter(([k, v]) => !['duedate', 'assignee'].includes(k) && v !== null)
                        );
                        if (Object.keys(filteredDryRunFields).length > 0) {
                          const existingFieldsIdx = otherArgs.findIndex(([k]) => k === 'fields');
                          if (existingFieldsIdx >= 0) {
                            otherArgs[existingFieldsIdx][1] = { ...otherArgs[existingFieldsIdx][1], ...filteredDryRunFields };
                          } else {
                            otherArgs.push(['fields', filteredDryRunFields]);
                          }
                        }

                        // Remove assigneeId if assignee exists (avoid duplicate)
                        const hasAssignee = otherArgs.some(([k]) => k === 'assignee');
                        if (hasAssignee) {
                          otherArgs = otherArgs.filter(([k]) => k !== 'assigneeId');
                        }

                        if (otherArgs.length === 0) return null;

                        // Helper to format user-friendly field names
                        const formatFieldName = (key) => {
                          if (process.env.REACT_APP_JIRA_NEED_TO_KNOW_FIELD && key === process.env.REACT_APP_JIRA_NEED_TO_KNOW_FIELD) return 'Need To Know';
                          if (key.startsWith('customfield_')) return `Custom Field ${key.replace('customfield_', '')}`;
                          if (key === 'assigneeId') return 'Assignee';
                          if (key === 'reporterId') return 'Reporter';
                          return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                        };

                        return (
                          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border-color)' }}>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: '500' }}>
                              Additional Fields
                            </div>
                            {otherArgs.map(([key, value]) => {
                              // Format the display value
                              let displayValue;
                              if (key === 'fields' && typeof value === 'object') {
                                // Special handling for custom fields - render each as separate line
                                return Object.entries(value).map(([fieldKey, fieldVal]) => {
                                  let fieldDisplay;
                                  if (Array.isArray(fieldVal)) {
                                    // Handle user arrays (like need-to-know)
                                    fieldDisplay = fieldVal.map(u => toDisplayName(u.accountId) || u.displayName || JSON.stringify(u)).join(', ');
                                  } else if (typeof fieldVal === 'object' && fieldVal?.accountId) {
                                    fieldDisplay = toDisplayName(fieldVal.accountId) || fieldVal.displayName;
                                  } else if (typeof fieldVal === 'object') {
                                    fieldDisplay = JSON.stringify(fieldVal);
                                  } else {
                                    fieldDisplay = String(fieldVal);
                                  }
                                  return (
                                    <div key={fieldKey} style={{ marginBottom: '6px' }}>
                                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{formatFieldName(fieldKey)}: </span>
                                      <span style={{ fontSize: '12px', color: 'var(--text-color)' }}>{fieldDisplay}</span>
                                    </div>
                                  );
                                });
                              } else if (typeof value === 'object' && value?.accountId) {
                                displayValue = toDisplayName(value.accountId) || value.displayName;
                              } else if (key === 'assignee' || key === 'reporter' || key === 'assigneeId' || key.toLowerCase().includes('accountid')) {
                                // Handle bare account ID strings for user fields
                                displayValue = typeof value === 'string' ? toDisplayName(value) : JSON.stringify(value);
                              } else if (typeof value === 'string' && /^\d+:[0-9a-f-]{36}$/.test(value)) {
                                // Looks like an account ID - try to convert it
                                displayValue = toDisplayName(value);
                              } else if (typeof value === 'object') {
                                displayValue = JSON.stringify(value, null, 2);
                              } else {
                                displayValue = String(value);
                              }

                              // Format key for display
                              const displayKey = formatFieldName(key);

                              return (
                                <div key={key} style={{ marginBottom: '6px' }}>
                                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{displayKey}: </span>
                                  <span style={{
                                    fontSize: '12px',
                                    color: 'var(--text-color)',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}>
                                    {displayValue}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Show JSON for other transaction types */}
                  {tx.name !== 'jira-create-issue' && (
                    <pre style={{
                      background: 'var(--bg-tertiary, #f5f5f5)',
                      padding: '10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      overflow: 'auto',
                      maxHeight: '150px',
                      color: 'var(--text-color)',
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
{JSON.stringify({
  args: Object.fromEntries(
    Object.entries(tx.args || {}).filter(([k]) => !['sys_token'].includes(k))
  ),
  dryRunResult: tx.dryRunResult?.params ? {
    ...tx.dryRunResult.params,
    sys_token: undefined
  } : tx.dryRunResult
}, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => setEditingTransaction(null)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  background: 'var(--accent-color, #007bff)',
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '14px'
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Input Modal */}
      {fullscreenInput && (
        <div
          className="fullscreen-input-overlay"
          style={{ top: viewportOffset, height: viewportHeight }}
        >
          <div className="fullscreen-input-modal">
            <div className="fullscreen-input-header">
              <button
                className="fullscreen-minimize-btn"
                onClick={() => {
                  setFullscreenInput(false);
                  setTimeout(() => {
                    if (textareaRef.current) {
                      textareaRef.current.focus();
                      const len = textareaRef.current.value.length;
                      textareaRef.current.setSelectionRange(len, len);
                    }
                  }, 0);
                }}
                title="Minimize"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 14h6v6M20 10h-6V4M10 14l-7 7M14 10l7-7"/>
                </svg>
              </button>
            </div>
            <textarea
              className="fullscreen-input-textarea"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              autoFocus
              onFocus={(e) => {
                // Move cursor to end
                const len = e.target.value.length;
                e.target.setSelectionRange(len, len);
              }}
              placeholder="Type your message..."
            />
            <div className="fullscreen-input-footer">
              <Recorder
                onBlobReady={(blob) => {
                  setFullscreenInput(false);
                  dictateAudio(blob);
                }}
                onRecordingChange={setRecordingState}
                start={start}
                stop={stop}
                recording={recording}
                starting={starting}
                supported={supported && pendingTransactions.length === 0}
                onStopClick={stopRecordingRef}
                onCancelClick={cancelRecordingRef}
              />
              <button
                className={`send-button ${inputText.trim() ? 'active' : ''}`}
                onClick={() => {
                  setFullscreenInput(false);
                  sendText();
                }}
                disabled={!inputText.trim() || pendingTransactions.length > 0}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="icon">
                  <path d="M8.99992 16V6.41407L5.70696 9.70704C5.31643 10.0976 4.68342 10.0976 4.29289 9.70704C3.90237 9.31652 3.90237 8.6835 4.29289 8.29298L9.29289 3.29298L9.36907 3.22462C9.76184 2.90427 10.3408 2.92686 10.707 3.29298L15.707 8.29298L15.7753 8.36915C16.0957 8.76192 16.0731 9.34092 15.707 9.70704C15.3408 10.0732 14.7618 10.0958 14.3691 9.7754L14.2929 9.70704L10.9999 6.41407V16C10.9999 16.5523 10.5522 17 9.99992 17C9.44764 17 8.99992 16.5523 8.99992 16Z"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      {showAllApps && <AllApps onBack={() => { setShowAllApps(false); setCurrentView('assistant'); window.location.hash = ''; }} />}
    </>
  );
}